/* eslint-disable prettier/prettier */
import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../components/supabase'

const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [organization, setOrganization] = useState(null)
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [error, setError] = useState(null)

  // Debug helper
  const log = (msg, data) => {
    console.log(`[Auth] ${msg}`, data || '')
  }

  useEffect(() => {
    log('Initializing auth...')
    
    // Check current session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      log('getSession result:', { session: !!session, error })
      
      if (error) {
        log('Session error:', error)
        setError(error)
        setLoading(false)
        return
      }

      if (session?.user) {
        log('Found existing session for:', session.user.email)
        setUser(session.user)
        setIsAuthenticated(true)
        
        // Fetch profile data
        fetchUserData(session.user.id).then(() => {
          log('User data fetched, setting loading=false')
          setLoading(false)
        })
      } else {
        log('No session found, setting loading=false')
        setLoading(false)
      }
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      log('Auth state changed:', event)
      
      if (event === 'SIGNED_IN' && session?.user) {
        log('User signed in:', session.user.email)
        setUser(session.user)
        setIsAuthenticated(true)
        fetchUserData(session.user.id)
      } else if (event === 'SIGNED_OUT') {
        log('User signed out')
        setUser(null)
        setProfile(null)
        setOrganization(null)
        setRoles([])
        setIsAuthenticated(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const fetchUserData = async (userId) => {
    log('Fetching user data for:', userId)
    
    try {
      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (profileError) {
        log('Profile fetch error:', profileError)
      } else {
        log('Profile loaded:', profileData?.username)
        setProfile(profileData)
      }

      // Fetch org role
      const { data: orgRoleData, error: roleError } = await supabase
        .from('user_organization_roles')
        .select(`
          role,
          department,
          job_title,
          organizations (id, name, slug, logo_url, apps_enabled)
        `)
        .eq('user_id', userId)
        .eq('is_active', true)
        .single()

      if (roleError) {
        log('Role fetch error:', roleError)
      } else {
        log('Role loaded:', orgRoleData?.role)
        setOrganization(orgRoleData?.organizations)
        setRoles([orgRoleData?.role])
      }
    } catch (err) {
      log('fetchUserData exception:', err)
    }
  }

  const signOut = async () => {
    log('Signing out...')
    await supabase.auth.signOut()
    setUser(null)
    setProfile(null)
    setOrganization(null)
    setRoles([])
    setIsAuthenticated(false)
  }

  const resetPassword = async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    return { data, error }
  }

  // Role helpers
  const getUserRole = () => {
    if (roles.length > 0) {
      const roleMap = {
        'owner': 'Manager',
        'manager': 'Manager', 
        'developer': 'Developer',
        'supervisor': 'Manager',
        'ride_operator': 'Ride Operator',
        'attraction_operator': 'Ride Operator',
        'foh_team': 'FOH Team',
        'fnb_team': 'F&B Team',
        'maintenance': 'Maintenance',
        'staff': 'Staff',
        'viewer': 'Viewer'
      }
      return roleMap[roles[0]] || roles[0]
    }
    return 'Unknown Role'
  }

  const getRawRole = () => roles.length > 0 ? roles[0] : null

  const hasRole = (requiredRole) => {
    const roleHierarchy = [
      'owner', 'manager', 'developer', 'supervisor',
      'ride_operator', 'attraction_operator', 'foh_team',
      'fnb_team', 'maintenance', 'staff', 'viewer'
    ]
    const rawRole = getRawRole()
    if (!rawRole) return false
    return roleHierarchy.indexOf(rawRole) <= roleHierarchy.indexOf(requiredRole)
  }

  const hasAdminAccess = () => ['owner', 'manager', 'developer'].includes(getRawRole())

  const getDepartment = () => {
    const rawRole = getRawRole()
    const roleDeptMap = {
      'owner': 'Management',
      'manager': 'Management',
      'developer': 'Developer',
      'ride_operator': 'Ride Operator',
      'attraction_operator': 'Ride Operator',
      'foh_team': 'FOH Team',
      'fnb_team': 'F&B Team',
      'maintenance': 'Maintenance',
      'staff': 'Staff',
      'viewer': 'Viewer'
    }
    return roleDeptMap[rawRole] || null
  }

  // Enriched user object
  const enrichedUser = user ? {
    ...user,
    'https://localhost:3000/roles': [getUserRole()],
    roles: [getUserRole()],
    role: getUserRole(),
    rawRole: getRawRole(),
    profile,
    organization,
    username: profile?.username,
    name: profile?.full_name || profile?.display_name || profile?.username || user.email,
    email: user.email,
    email_verified: user.email_confirmed_at !== null,
    picture: profile?.avatar_url,
    sub: user.id,
    updated_at: profile?.updated_at || user.updated_at,
  } : null

  // Debug current state
  log('Current state:', { loading, isAuthenticated, hasUser: !!user })

  const value = {
    user: enrichedUser,
    profile,
    organization,
    roles,
    loading,
    isLoading: loading,
    isAuthenticated,
    error,
    signOut,
    logout: signOut,
    resetPassword,
    getUserRole,
    getRawRole,
    hasRole,
    hasAdminAccess,
    getDepartment,
    refreshUser: () => user?.id && fetchUserData(user.id)
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext